import { useState, type ReactNode } from 'react';
import { Button, Stack, Text } from '@chakra-ui/react';
import type { CloudDevice } from '../data/cloud_secrets.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeError } from '../ui/chrome_page.tsx';

// Letting a device into the home (docs/specs/cloud-joining.md JN-4; secrets SE-13, QSE-7): it gets
// the key to the passwords the dashboards use, once its code is the one it shows.

function messageOf(error: unknown) : string {
    return error instanceof Error ? error.message : String(error);
}

export function AllowDeviceDialog({ device, code, onAllow, onClose }: { device: CloudDevice; code: string; onAllow: () => Promise<void>; onClose: () => void }) : ReactNode {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    return (
        <ChromeDialog
            title={`"${device.name}" wants to join your home`}
            onClose={onClose}
            size="sm"
            isQuestion
            footer={
                <>
                    <Button variant="outline" onClick={onClose}>
                        Not now
                    </Button>
                    <Button
                        colorPalette="blue"
                        loading={busy}
                        onClick={() => {
                            setBusy(true);
                            onAllow().catch((caught: unknown) => {
                                setError(messageOf(caught));
                                setBusy(false);
                            });
                        }}
                    >
                        Allow
                    </Button>
                </>
            }
        >
            <Stack gap="4">
                <Text>Does it show this code?</Text>
                <Text fontFamily="mono" fontSize="2xl" fontWeight="bold" textAlign="center" data-testid="allow-code">
                    {code}
                </Text>
                <Text color="fg.muted" fontSize="sm">
                    Only allow it if it is yours: it gets the passwords your dashboards use.
                </Text>
                {error ? <ChromeError text={error} /> : null}
            </Stack>
        </ChromeDialog>
    );
}
